import { Button, Stack, Typography } from "@mui/material";
import { BadgeType, MeetingInfoBadge } from "../MeetingInfoBadge";
import { DetailField } from "./DetailField";
import React from "react";
import { InstanceMetadataEditDialog } from "../metadata_editing/InstanceMetadataEditDialog";
import { HostedMeetingFallbackReadOnly } from "./HostedMeetingFallbackReadOnly";
import {
  descriptionToString,
  getDateString,
  getTimeString,
  hasApparentOverride,
} from "../../helpers";
import { Meeting } from "../../../../model/meeting";
import { useBoundStore } from "../../../../store/store";

interface InstanceDetailsViewProps {
  meeting: Meeting;
}

export const toInstanceDetail = (meeting: Meeting) => {
  const { recurringMeeting } = meeting;

  const meetingTypeId = meeting.metadata.meetingType;

  const host = meeting.host;
  const missingData = !meetingTypeId || !host;
  const visible = meeting.metadata.memberVisible;
  const differentFromRecurring = recurringMeeting
    ? hasApparentOverride(meeting)
    : undefined;

  return {
    title: meeting.title,
    meetingTypeId,
    date: getDateString(meeting.start),
    time: getTimeString(meeting.start, meeting.end),
    description: descriptionToString(meeting.description),
    isRecurring: recurringMeeting ? true : false,
    hostName: host?.displayName,
    isVisible: visible ?? false,
    isMissingData: missingData,
    isDifferentFromRecurring: differentFromRecurring,
    // Instance override wins; otherwise the key set on the series applies.
    roomKey:
      meeting.metadata.continuousRoomKey ||
      recurringMeeting?.metadata.continuousRoomKey,
  };
};

export const InstanceDetailsView: React.FC<InstanceDetailsViewProps> = ({
  meeting,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [fallbackRefreshKey, setFallbackRefreshKey] = React.useState(0);
  const getMeetingType = useBoundStore((state) => state.getTypeById);

  const {
    title,
    date,
    time,
    meetingTypeId,
    hostName,
    isMissingData,
    isVisible,
    description,
    roomKey,
  } = toInstanceDetail(meeting);
  const meetingTypeInfo = meetingTypeId ? getMeetingType(meetingTypeId) : undefined;
  const meetingType = meetingTypeInfo?.title;
  const airtableDescription = meetingTypeInfo?.description;

  function handleDialogClose() {
    setIsEditing(false);
    setFallbackRefreshKey((k) => k + 1);
  }

  return (
    <Stack gap={2} padding="20px 60px">
      <Stack
        gap={1}
        flex={1}
        alignItems="flex-end"
        direction="row"
        marginLeft="-20px"
      >
        {!isVisible && <MeetingInfoBadge type={BadgeType.notVisible} />}
        {isMissingData && <MeetingInfoBadge type={BadgeType.missingData} />}
      </Stack>

      <Stack gap={2} direction="row" alignItems="baseline">
        <Typography variant="h4" fontWeight="bold">
          {title}
        </Typography>
        <Button
          variant="text"
          onClick={() => setIsEditing(true)}
          sx={{ marginTop: "10px" }}
        >
          Edit Instance
        </Button>
      </Stack>
      <DetailField field="id" value={meeting.id} />
      <DetailField field="date" value={date} />
      <DetailField field="time" value={time} />
      <DetailField field="hostName" value={hostName} />
      <DetailField field="meetingType" value={meetingType} />
      {roomKey && <DetailField field="continuousRoomKey" value={roomKey} />}
      <DetailField field="description" value={airtableDescription || description} />
      <HostedMeetingFallbackReadOnly
        gcalEventId={meeting.id}
        refreshKey={fallbackRefreshKey}
      />
      <InstanceMetadataEditDialog
        meeting={meeting}
        open={isEditing}
        onClose={handleDialogClose}
      />
    </Stack>
  );
};
